import React, { useState, useMemo } from "react";
import HoverChat from "../../components/HoverChat";
import { computeGcpp } from "./calc";
import { Field, inputStyle } from "./StepEnergy";

/**
 * GCPP Savings Model — the secondary drawer that expands from Step 3.
 *
 * Recomputes the landed cost using the captive-ownership formula from
 * `reference/Green_Gold_Grid_Tabs.html` — ₹8–12/kWh grid → ₹4–5.50/kWh
 * landed, scaled by how far the equity + consumption shares exceed the
 * regulatory minimums (26% equity, 51% consumption).
 */
export default function GcppModel({ baseValues, onSubmitRfp }) {
  const [equity, setEquity] = useState(26);
  const [consumption, setConsumption] = useState(51);
  const gcpp = useMemo(
    () =>
      computeGcpp({
        tariff: baseValues.tariff,
        equityPct: equity,
        consumptionPct: consumption,
      }),
    [baseValues.tariff, equity, consumption]
  );

  return (
    <div
      data-testid="audit-gcpp-panel"
      style={{
        marginTop: 32,
        padding: "26px 26px 30px",
        borderRadius: "var(--radius-lg)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.01))",
        border: "1px solid var(--line)",
      }}
    >
      <div className="gg-eyebrow" style={{ marginBottom: 8 }}>
        Secondary path · Captive ownership
      </div>
      <h3 className="gg-h2" style={{ marginBottom: 12 }}>
        The GCPP Savings Model.
      </h3>
      <p
        style={{
          color: "var(--text-dim)",
          fontSize: 15,
          lineHeight: 1.7,
          maxWidth: 720,
        }}
      >
        Under the Supreme-Court-confirmed{" "}
        <HoverChat term="gcpp">Group Captive framework</HoverChat>, an industry
        holding ≥26% equity in the plant and consuming ≥51% of its output
        escapes cross-subsidy — landed cost falls from ₹8–12 to ₹4–5.50/kWh,
        with <HoverChat term="cbam">CBAM</HoverChat>-grade traceable green
        power built in for exporters. Try the levers.
      </p>

      <div
        style={{
          marginTop: 22,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
          maxWidth: 640,
        }}
      >
        <Field
          label="Captive equity share (%)"
          hint="Minimum 26% to qualify."
        >
          <input
            type="range"
            min={0}
            max={100}
            value={equity}
            onChange={(e) => setEquity(parseInt(e.target.value, 10))}
            data-testid="audit-gcpp-equity"
            style={{ width: "100%", accentColor: "var(--gold)" }}
          />
          <span
            style={{ color: "var(--gold-soft)", fontSize: 14 }}
            data-testid="audit-gcpp-equity-value"
          >
            {equity}%
          </span>
        </Field>
        <Field
          label="Consumption share (%)"
          hint="Minimum 51% to qualify."
        >
          <input
            type="range"
            min={0}
            max={100}
            value={consumption}
            onChange={(e) => setConsumption(parseInt(e.target.value, 10))}
            data-testid="audit-gcpp-consumption"
            style={{ width: "100%", accentColor: "var(--gold)" }}
          />
          <span
            style={{ color: "var(--gold-soft)", fontSize: 14 }}
            data-testid="audit-gcpp-consumption-value"
          >
            {consumption}%
          </span>
        </Field>
      </div>

      <div
        style={{
          marginTop: 24,
          padding: 18,
          borderRadius: 12,
          background: gcpp.qualifies
            ? "linear-gradient(180deg, rgba(244,169,31,0.12), rgba(244,169,31,0.02))"
            : "rgba(255,255,255,0.03)",
          border: `1px solid ${
            gcpp.qualifies ? "rgba(244,169,31,0.4)" : "var(--line)"
          }`,
        }}
        data-testid="audit-gcpp-result"
      >
        {gcpp.qualifies ? (
          <p
            style={{
              margin: 0,
              color: "var(--text-cloud)",
              fontSize: 16,
              lineHeight: 1.7,
            }}
          >
            You qualify. Landed cost drops from{" "}
            <strong>₹{baseValues.tariff}/kWh</strong> to{" "}
            <strong style={{ color: "var(--gold-soft)" }}>
              ₹{gcpp.landedInrPerKwh}/kWh
            </strong>{" "}
            — a <strong>{gcpp.savingsPct}%</strong> compression, or roughly
            ₹{gcpp.savingsPerKwh}/kWh saved on every unit you consume.
          </p>
        ) : (
          <p
            style={{
              margin: 0,
              color: "var(--text-dim)",
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            Below the qualification floor. You need ≥26% equity{" "}
            <em>and</em> ≥51% consumption to escape cross-subsidy under the
            GCPP framework. Move both sliders up to see the compression.
          </p>
        )}
      </div>

      <p
        style={{
          marginTop: 22,
          color: "var(--text-cloud)",
          fontSize: 15.5,
          lineHeight: 1.7,
        }}
      >
        When the numbers make sense,{" "}
        <button
          type="button"
          className="gg-action"
          onClick={onSubmitRfp}
          data-testid="audit-gcpp-open-rfp"
          style={{
            all: "unset",
            cursor: "pointer",
            color: "var(--gold)",
            backgroundImage: "linear-gradient(90deg, var(--gold), var(--gold-deep))",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "0 100%",
            backgroundSize: "100% 1px",
            paddingBottom: 2,
            fontWeight: 500,
          }}
        >
          send us an RFP flagged as a GCPP engagement
        </button>{" "}
        and we'll model the structure end to end.
      </p>
    </div>
  );
}
